import styled from '@emotion/styled'
import React, { useEffect, useState } from 'react'
import { ResponsiveBar } from '@nivo/bar'
import { AxisTickProps } from '@nivo/axes'
import {
  IGenericObject,
  IDimension,
  ITimeScale,
  ITrendLine,
  AxisLegend,
  IMargin,
} from '@types'
import { LegendProps } from '@nivo/legends'
import { line, curveMonotoneY } from 'd3-shape'
import { Spinner } from '@ss/meta-core-components'
import { initTheme, MAIN_PALETTE, NumberFormatter } from '@utils'
import { DateTime } from 'luxon'

export interface Props {
  data?: IGenericObject<string | number>[]
  keys?: string[]
  dataCy?: string
  dimensions?: IDimension
  PDF?: ITrendLine
  indexBy?: string
  timeScale?: ITimeScale
  axisLegend?: AxisLegend
  loading?: boolean
  barPadding?: number
  loadingSpinner?: JSX.Element
  theme?: 'dark' | 'light'
  margin?: IMargin
  groupMode?: 'stacked' | 'grouped'
  enableLabel?: boolean
  customColors?: string[]
  customLegend?: LegendProps[]
  bin?: number
  layout?: 'horizontal' | 'vertical'
}

/**
 * Componente custom per un grafico a barre
 * @param props.data {Array<IGenericObject<string | number>>}
 * @param props.keys {(string[])=} default ['y']
 * @param props.dataCy {string=} support test e2e
 * @param props.dimensions {IDimension= } dimensioni del container
 * @param props.PDF {ITrendLine= } coordinate per la mediana
 * @param props.indexBy {string=} nome del campo da cui prendere il valore sull asse x default 'x'
 * @param props.timeScale {ITimeScale= } flag per time scale
 * @param props.axisLegend {AxisLegend= } legenda per il dato del asse x e del asse y
 * @param props.barPadding {number=} distanza tra una barra e l'altra del grafico valore da 0 a 1 default 0
 * @param props.loadingSpinner {JSX.Element=} spinner personalizzato
 * @param props.theme {('dark' | 'light')=} tipo del tema
 * @param props.margin {IMargin=} permette di customizzare i margini
 * @param props.groupMode {('stacked' | 'grouped')=} permette di mettere le barre in pila o meno
 * @param props.enableLabel {boolean=} permette di mostrare o meno il testo sulla barra
 * @param props.customColors {Array<string> } permette di personalizzare i colori
 * @param props.customLegend {Array<any>} permette di customizzare la legenda
 * @param props.bin {number=} numero di bin presenti nel grafico necessario per bin alti
 * @param props.layout {('horizontal'|'vertical')=} layout del grafico
 * @component
 */
export const Bar: React.FC<Props> = (props) => {
  const {
    bin,
    data,
    keys = ['y'],
    dataCy = '',
    dimensions = { height: '375px', width: '100%' },
    indexBy = 'x',
    PDF,
    layout = 'vertical',
    timeScale,
    axisLegend = { x: undefined, y: undefined },
    barPadding = 0,
    loadingSpinner,
    theme = 'dark',
    groupMode,
    enableLabel,
    customColors,
    customLegend,
  } = props

  const [padding, setPadding] = useState(0)

  const [ticks, setTicks] = useState<(string | number)[]>([])

  useEffect(() => {
    if (!data) {
      setTicks([])
      return
    }

    const keys = data.map((d) => d.x)
    if (!bin || bin <= 20) {
      setTicks(keys)
      return
    }
    const newKey: (string | number)[] = []
    keys.forEach((k, index) => {
      if (bin >= 200) {
        if (index % Math.round(keys.length / 9) === 0) {
          newKey.push(k)
          return
        }
      } else if (index % Math.round(keys.length / 20) === 0) {
        newKey.push(k)
        return
      }
    })
    setTicks(newKey)
  }, [bin, data])

  useEffect(() => {
    if (barPadding <= 0) setPadding(0)
    else if (barPadding >= 1) setPadding(1)
    else setPadding(barPadding)
  }, [])

  const lineLayer = (data: {
    xScale: Function
    yScale: Function
    bars: IGenericObject[]
  }): JSX.Element => {
    if (!PDF || PDF.length === 0) return <></>
    const lineGenerator = line()
      .curve(curveMonotoneY)
      .x((d: number[], index) => data.xScale(d[0]) + data.bars[index].width / 2)
      .y((d: number[]) => data.yScale(d[1]))

    return (
      <path
        d={lineGenerator(PDF) || undefined}
        fill={'transparent'}
        style={{ pointerEvents: 'none' }}
        strokeWidth={2}
        stroke={theme === 'dark' ? 'white' : 'black'}
      />
    )
  }
  const layers = [
    'grid',
    'axes',
    'bars',
    lineLayer,
    'markers',
    'legends',
    'annotations',
  ]

  const chartProps: any = {
    margin: props.margin || { top: 60, right: 140, bottom: 70, left: 90 },
    data,
    indexBy,
    layout,
    keys,
    groupMode: groupMode || 'grouped',
    padding,
    labelSkipWidth: 16,
    labelSkipHeight: 16,
    layers,
    enableLabel,
    isInteractive: bin && bin <= 201,
    legendLabel: (data: any) => `${axisLegend[data.id] || data.id}`,
    tooltipLabel: (data: any) => `${axisLegend[data.id] || data.id}`,
    colors: customColors || MAIN_PALETTE,
    axisBottom:
      layout !== 'horizontal'
        ? {
            legend: axisLegend.x,
            legendPosition: 'middle',
            legendOffset: 46,
            tickValues: ticks,
            tickRotation: bin && bin > 20 ? 18 : 0,
            format: (value: number | string) => {
              if (typeof value === 'number') {
                if (value <= 0) return NumberFormatter(value, 'KGM')
                if (value < 0.001) return NumberFormatter(value, 'EXP')
                if (value > 9999) return NumberFormatter(value, 'KGM')
                return value
              }
              return value
            },
          }
        : {
            format: (value: number | string) => {
              if (typeof value === 'number') {
                if (value <= 0) return NumberFormatter(value, 'KGM')
                if (value < 0.001) return NumberFormatter(value, 'EXP')
                if (value > 9999) return NumberFormatter(value, 'KGM')
                return value
              }
              return value
            },
          },
    valueFormat: (value: number | string) => {
      if (typeof value === 'number') {
        if (value <= 0) return NumberFormatter(value, 'KGM')
        if (value < 0.001) return NumberFormatter(value, 'EXP')
        if (value > 9999) return NumberFormatter(value, 'KGM')
        return value
      }
      return value
    },
  }
  const timeAxis = timeScale
    ? {
        [`${timeScale.axis}Scale`]: {
          type: 'time',
          format: timeScale.format,
          useUTC: false,
          precision: 'day',
        },
        [`${timeScale.axis}Format`]: `time:${timeScale.format}`,
        [`axis${timeScale.axis === 'x' ? 'Bottom' : 'Left'}`]: {
          tickValues: 'every 2 days',
          legend: axisLegend.x || 'Time',
          legendPosition: 'middle',
          legendOffset: 46,
          format: (value: string) => {
            return DateTime.fromISO(value).toFormat('dd/MM/yyyy')
          },
        },
      }
    : {
        axisLeft: {
          orient: 'left',
          legendPosition: 'middle',
          legendOffset: -75,
          renderTick:
            layout === 'horizontal'
              ? (tick: AxisTickProps<string>) => {
                  return (
                    <g transform={`translate(${tick.x - 75},${tick.y})`}>
                      <rect
                        x={-14}
                        y={-6}
                        rx={3}
                        ry={3}
                        width={28}
                        height={24}
                        fill='transparent'
                      />
                      <rect
                        x={-12}
                        y={-12}
                        rx={2}
                        ry={2}
                        width={24}
                        height={24}
                        fill='transparent'
                      />
                      <text
                        textAnchor='middle'
                        dominantBaseline='middle'
                        style={{
                          fill: 'var(--text)',
                          fontSize: 14,
                        }}
                      >
                        <title>{tick.value.toUpperCase()}</title>
                        {tick.value.length > 13
                          ? tick.value.substring(0, 13).toUpperCase() + '...'
                          : tick.value.toUpperCase()}
                      </text>
                    </g>
                  )
                }
              : undefined,
          format: (value: number | string) => {
            if (typeof value === 'number') {
              if (value <= 0) return NumberFormatter(value, 'KGM')
              if (value < 0.001) return NumberFormatter(value, 'EXP')
              if (value > 9999) return NumberFormatter(value, 'KGM')
              return value
            }
            return value
          },
        },
      }

  const legends: LegendProps[] = customLegend || [
    {
      anchor: 'bottom-right',
      direction: 'column',
      justify: false,
      translateX: 120,
      translateY: 0,
      itemsSpacing: 2,
      itemWidth: 100,
      itemHeight: 20,
      itemDirection: 'left-to-right',
      itemOpacity: 0.85,
      symbolSize: 18,
      symbolShape: 'square',
      effects: [
        {
          on: 'hover',
          style: {
            itemOpacity: 1,
          },
        },
      ],
    },
  ]

  return (
    <Container
      height={dimensions.height || '375px'}
      width={dimensions.width || '100%'}
      data-cy={`${dataCy}-bar`}
      loading={!data}
    >
      {data ? (
        <ResponsiveBar
          {...chartProps}
          legends={legends}
          theme={initTheme(theme)}
          {...timeAxis}
        />
      ) : (
        <Loading>
          <Loading>
            {loadingSpinner ? (
              loadingSpinner
            ) : (
              <Spinner data-cy={`${dataCy}-bar-loading`} />
            )}
          </Loading>
          <Loading>LOADING</Loading>
        </Loading>
      )}
    </Container>
  )
}

//region Style
const Container = styled.div<{
  height: string
  width: string
  loading?: boolean
}>`
  height: ${({ height }) => height};
  width: ${({ width }) => width};
  ${({ loading }) => loading && 'display:grid'};
`
const Loading = styled.div`
  display: grid;
  place-self: center;
  gap: 10px;
`
//endregion
